// Botón "Enviar a lanzamiento" + su popover, anclado donde se tocó.
//
// Reemplaza a "Descargar" como acción primaria en las barras de imagen cuando la
// empresa tiene prendida la función (ADR-2026-09-06-enviar-a-lanzamiento).
// Encapsula TODO el flujo para que integrarlo en `GeneratedImagesInline` y
// `GeneratedImagesHistory` —dos archivos de ~7000 líneas— sea una línea y no una
// cuarta copia de un menú.
//
// Qué decide el popover, en orden:
//   sin lanzamientos  → invita a crear uno, y al crearlo sigue con el envío
//   uno solo          → manda derecho, sin preguntar
//   varios            → lista para elegir
// y en paralelo, a qué producto va la imagen (`destinoDeImagen`):
//   un producto       → ése
//   varios            → elegir entre esos, en el mismo popover
//   ninguno           → panel lateral (desktop) / hoja inferior (mobile) con todo
//                       el catálogo de la marca
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Rocket, Plus, Check, Loader2, ChevronRight, Search } from "lucide-react";
import CachedImg from "./CachedImg";
import { SheetLateral } from "./SheetLateral";
import { supabase } from "../lib/supabase";
import { destinoDeImagen, variantesDeImagen, type FilaConVariantes } from "../utils/variantesDeImagen";
import { posicionDePopover, type PosicionPopover } from "../utils/posicionDePopover";
import {
  crearLanzamiento,
  enviarImagenesALanzamiento,
  fetchLanzamientos,
  productosDeVariantes,
  type CanalLanzamiento,
  type ItemAEnviar,
  type Lanzamiento,
  type ProductoResuelto,
} from "../pages/lanzamientos/lanzamientosApi";
import { CANALES, canalesPorGrupo, nombresDeDestinos } from "../pages/lanzamientos/canales";
import { useMercadoLibreDisponible } from "../hooks/useMercadoLibreDisponible";
import IconoDeCanal from "../pages/lanzamientos/IconoDeCanal";

/** Una imagen candidata a viajar al lanzamiento. */
export interface ImagenParaLanzamiento extends FilaConVariantes {
  id: string;
  fileName?: string | null;
  /** Los videos no se mandan: un lanzamiento sube fotos de producto. */
  isVideo?: boolean;
}

export interface EnviarALanzamientoProps {
  companyId: string | null;
  /**
   * Marca activa. Si se omite, el componente la resuelve solo al abrir
   * (`profiles.current_brand_id`): los hosts son archivos enormes que no la tienen
   * a mano y no vale la pena hacerles cargar un hook entero por un botón.
   */
  brandId?: string | null;
  /** Las imágenes seleccionadas en la barra donde vive el botón. */
  imagenes: ImagenParaLanzamiento[];
  /** Clases del botón: cada barra tiene su propio look (primario azul, glass, etc.). */
  className?: string;
  /** Hacia dónde se abre el popover. Las barras de acción viven abajo. */
  direccion?: "arriba" | "abajo";
  /**
   * Clases del contenedor. En una barra HORIZONTAL hace falta `shrink-0`: sin eso
   * flex encoge el envoltorio, el botón queda angosto y la etiqueta se parte en dos
   * renglones.
   */
  contenedorClassName?: string;
  /** Tamaño del ícono. Las barras horizontales usan w-4 h-4; las de columna, w-5 h-5. */
  tamanoIcono?: string;
  /**
   * De qué lado del botón se ancla el popover. El botón del visor de escritorio es
   * el último de la barra: anclado a la izquierda, el panel se saldría de pantalla.
   */
  alineacion?: "izquierda" | "derecha";
  /** Aviso para el toast del host. */
  onAviso?: (mensaje: string, lanzamientoId?: string) => void;
  /** Para navegar al lanzamiento desde el toast del host. */
  disabled?: boolean;
  label?: string;
  /**
   * Clases del TEXTO del botón. Con `sr-only` el botón queda solo con el ícono pero
   * conserva su nombre accesible y el tooltip — es lo que necesitan las cards de la
   * columna de resultados, donde no hay lugar para una etiqueta
   * (ADR-2026-09-07-acciones-rapidas-en-la-card).
   */
  labelClassName?: string;
}

type Paso =
  | { tipo: "cargando" }
  | { tipo: "lanzamientos" }
  | { tipo: "crear" }
  | { tipo: "elegir-producto"; opciones: ProductoResuelto[]; lanzamientoId: string }
  | { tipo: "enviando" };

/**
 * Ancho del popover. Es una constante y no sólo la clase `w-[280px]` porque la cuenta de
 * la posición lo necesita: portaleado al body, alinearlo por su borde derecho es restar.
 */
const ANCHO_PANEL = 280;

export default function EnviarALanzamiento({
  companyId,
  brandId,
  imagenes,
  className = "",
  direccion = "arriba",
  contenedorClassName = "",
  tamanoIcono = "w-5 h-5",
  alineacion = "izquierda",
  onAviso,
  disabled,
  label = "Enviar a lanzamiento",
  labelClassName = "",
}: EnviarALanzamientoProps) {
  const [marcaResuelta, setMarcaResuelta] = useState<string | null>(brandId ?? null);
  const [abierto, setAbierto] = useState(false);
  const [paso, setPaso] = useState<Paso>({ tipo: "cargando" });
  const [lanzamientos, setLanzamientos] = useState<Lanzamiento[]>([]);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [canalNuevo, setCanalNuevo] = useState<CanalLanzamiento>("tiendanube");
  // Mercado Libre se ofrece sólo si la marca lo puede usar (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
  // Acá se elige UNO; sumar la otra tienda se hace después, en el lanzamiento.
  const ml = useMercadoLibreDisponible(marcaResuelta);
  const [sheetAbierta, setSheetAbierta] = useState(false);
  const [lanzamientoPendiente, setLanzamientoPendiente] = useState<string | null>(null);
  const [errorLocal, setErrorLocal] = useState<string | null>(null);
  // Aviso propio: los hosts son barras de acción sin sistema de toast, así que el
  // componente se hace cargo de confirmar. `onAviso` sigue existiendo para el que
  // quiera mostrarlo a su manera.
  const [avisoPropio, setAvisoPropio] = useState<{ texto: string; id: string } | null>(null);
  const contenedorRef = useRef<HTMLDivElement | null>(null);
  // El popover se portalea al body, así que necesita su propia ref (para el click
  // afuera, que ya no lo encuentra dentro del contenedor) y la del botón, que es el
  // ancla de la cuenta — ADR-2026-09-13-el-popover-de-lanzamiento-se-portalea.
  const botonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [posicion, setPosicion] = useState<PosicionPopover | null>(null);

  // Solo fotos: un video no es una foto de producto.
  const fotos = useMemo(() => imagenes.filter((i) => !i.isVideo), [imagenes]);

  // Las variantes de TODAS las fotos seleccionadas, sin repetir.
  const variantes = useMemo(() => {
    const vistos = new Set<string>();
    for (const img of fotos) for (const v of variantesDeImagen(img)) vistos.add(v);
    return [...vistos];
  }, [fotos]);

  const cerrar = useCallback(() => {
    setAbierto(false);
    setErrorLocal(null);
    setNombreNuevo("");
  }, []);

  // Click afuera y Escape — el popover vive dentro de barras que ya tienen sus
  // propios overlays, así que se cierra solo antes de estorbar.
  useEffect(() => {
    if (!abierto) return;
    const alTocarAfuera = (e: MouseEvent) => {
      const t = e.target as Node;
      // El panel ya no está dentro del contenedor (se portalea al body): sin mirarlo
      // también, el primer clic en una opción se leería como "clic afuera" y cerraría.
      if (contenedorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      cerrar();
    };
    const alaTecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") cerrar();
    };
    document.addEventListener("mousedown", alTocarAfuera);
    document.addEventListener("keydown", alaTecla);
    return () => {
      document.removeEventListener("mousedown", alTocarAfuera);
      document.removeEventListener("keydown", alaTecla);
    };
  }, [abierto, cerrar]);

  /**
   * Dónde va el panel, en coordenadas de pantalla. Se recalcula con el scroll de
   * CUALQUIER contenedor (por eso en captura: los eventos de scroll no burbujean) y con
   * el resize, así el panel sigue al botón en vez de quedar flotando donde abrió.
   */
  useLayoutEffect(() => {
    if (!abierto) {
      setPosicion(null);
      return;
    }
    const recalcular = () => {
      const caja = botonRef.current?.getBoundingClientRect();
      if (!caja) return;
      setPosicion(
        posicionDePopover(caja, {
          direccion,
          alineacion,
          ancho: ANCHO_PANEL,
          viewport: { ancho: window.innerWidth, alto: window.innerHeight },
        }),
      );
    };
    recalcular();
    window.addEventListener("scroll", recalcular, true);
    window.addEventListener("resize", recalcular);
    return () => {
      window.removeEventListener("scroll", recalcular, true);
      window.removeEventListener("resize", recalcular);
    };
  }, [abierto, direccion, alineacion]);

  /** Manda las fotos, resolviendo antes a qué producto van. */
  const enviar = useCallback(
    async (lanzamientoId: string, productoForzado?: string) => {
      if (!companyId) return;
      setPaso({ tipo: "enviando" });
      setErrorLocal(null);

      const items: ItemAEnviar[] = [];
      if (productoForzado) {
        for (const f of fotos) {
          const v = variantesDeImagen(f);
          items.push({ imagenGeneradaId: f.id, productoId: productoForzado, varianteId: v[0] ?? null });
        }
      } else {
        // Cada foto va al producto con el que se generó.
        const porVariante = await productosDeVariantes(variantes);
        const productoDeVariante = new Map<string, string>();
        for (const p of porVariante) productoDeVariante.set(p.varianteId, p.productoId);
        // productosDeVariantes deduplica por producto, así que hay que mapear el
        // resto de las variantes por su cuenta.
        const faltantes = variantes.filter((v) => !productoDeVariante.has(v));
        if (faltantes.length > 0) {
          const extra = await productosDeVariantes(faltantes);
          for (const p of extra) productoDeVariante.set(p.varianteId, p.productoId);
        }
        for (const f of fotos) {
          const vs = variantesDeImagen(f);
          const productoId = vs.map((v) => productoDeVariante.get(v)).find(Boolean);
          if (!productoId) continue;
          items.push({ imagenGeneradaId: f.id, productoId, varianteId: vs[0] ?? null });
        }
      }

      if (items.length === 0) {
        setErrorLocal("No se pudo resolver el producto de la imagen");
        setPaso({ tipo: "lanzamientos" });
        return;
      }

      const r = await enviarImagenesALanzamiento(lanzamientoId, companyId, items);
      if (r.error) {
        setErrorLocal(r.error);
        setPaso({ tipo: "lanzamientos" });
        return;
      }
      cerrar();
      setSheetAbierta(false);
      const nombre = lanzamientos.find((l) => l.id === lanzamientoId)?.nombre ?? "el lanzamiento";
      const msg =
        r.agregadas === 0
          ? `Ya estaba${items.length > 1 ? "n" : ""} en ${nombre}`
          : `${r.agregadas === 1 ? "Imagen enviada" : `${r.agregadas} imágenes enviadas`} a ${nombre}`;
      setAvisoPropio({ texto: msg, id: lanzamientoId });
      window.setTimeout(() => setAvisoPropio(null), 5000);
      onAviso?.(msg, lanzamientoId);
    },
    [companyId, fotos, variantes, lanzamientos, cerrar, onAviso],
  );

  /** Elegido el lanzamiento, falta saber a qué producto va. */
  const seguirConProducto = useCallback(
    async (lanzamientoId: string) => {
      const productos = await productosDeVariantes(variantes);
      const destino = destinoDeImagen(productos.map((p) => p.productoId));
      if (destino.tipo === "directo") {
        void enviar(lanzamientoId, destino.productoId);
        return;
      }
      if (destino.tipo === "elegir-entre-estos") {
        // Cada foto puede tener su propio producto: si todas coinciden en uno solo
        // no hay nada que preguntar, y eso ya lo cubre la rama de arriba.
        setPaso({ tipo: "elegir-producto", opciones: await conMiniaturas(productos), lanzamientoId });
        return;
      }
      // Sin producto: el catálogo entero, en el panel lateral.
      setLanzamientoPendiente(lanzamientoId);
      setAbierto(false);
      setSheetAbierta(true);
    },
    [variantes, enviar],
  );

  const abrir = useCallback(async () => {
    if (!companyId || fotos.length === 0) return;
    setAbierto(true);
    setPaso({ tipo: "cargando" });
    const marca = brandId !== undefined ? brandId : await resolverMarcaActiva();
    setMarcaResuelta(marca);
    const ls = (await fetchLanzamientos(companyId, marca)).filter((l) => l.estado !== "publicado");
    setLanzamientos(ls);
    if (ls.length === 0) {
      setNombreNuevo(sugerirNombre());
      setPaso({ tipo: "crear" });
      return;
    }
    if (ls.length === 1) {
      void seguirConProducto(ls[0]!.id);
      return;
    }
    setPaso({ tipo: "lanzamientos" });
  }, [companyId, brandId, fotos.length, seguirConProducto]);

  const crear = useCallback(async () => {
    if (!companyId) return;
    setPaso({ tipo: "enviando" });
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const nuevo = await crearLanzamiento({
      companyId,
      brandId: marcaResuelta,
      nombre: nombreNuevo,
      canal: canalNuevo,
      userId: user?.id ?? null,
    });
    if (!nuevo) {
      setErrorLocal("No se pudo crear el lanzamiento");
      setPaso({ tipo: "crear" });
      return;
    }
    setLanzamientos((prev) => [nuevo, ...prev]);
    void seguirConProducto(nuevo.id);
  }, [companyId, marcaResuelta, nombreNuevo, canalNuevo, seguirConProducto]);

  /**
   * Sin empresa no hay a dónde mandar nada, y ahí no dibujarse es correcto.
   *
   * Con la lista VACÍA, en cambio, solo se desaparece si el host no dijo nada:
   * un host que manda `disabled` ya decidió que el botón se ve apagado —es lo que
   * hace la barra de selección cuando no hay nada tildado— y hacerlo desaparecer
   * le deja un hueco sin ningún botón, porque su ternario ya eligió éste en vez
   * del de descargar. Así se rompió "enviar a lanzamiento" en las creaciones
   * anteriores del inline (ADR-2026-09-13-lanzamiento-en-creaciones-anteriores):
   * un bug de datos se volvió invisible en vez de verse apagado.
   */
  if (!companyId) return null;
  if (fotos.length === 0 && disabled === undefined) return null;

  const panel = (
    // `fixed` + portal al body: adentro de la card lo recortaba el contenedor que
    // scrollea la grilla (ADR-2026-09-13-el-popover-de-lanzamiento-se-portalea). El
    // z-index tiene que pasar el sheet de la columna de resultados, que es z-[100], y
    // quedar por debajo del panel lateral de productos (z-[210]).
    <div
      ref={panelRef}
      className="fixed z-[200] w-[280px] rounded-2xl border border-neutral-200 bg-white shadow-xl overflow-y-auto overscroll-contain"
      style={{
        maxWidth: "calc(100vw - 2rem)",
        left: posicion?.left ?? 0,
        top: posicion?.top ?? undefined,
        bottom: posicion?.bottom ?? undefined,
        maxHeight: posicion?.maxAlto,
      }}
      role="menu"
    >
      {paso.tipo === "cargando" || paso.tipo === "enviando" ? (
        <div className="flex items-center gap-2 py-4 px-5 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          {paso.tipo === "enviando" ? "Enviando…" : "Buscando tus lanzamientos…"}
        </div>
      ) : paso.tipo === "crear" ? (
        <div className="py-3 px-4">
          <p className="text-sm font-semibold text-brand-black">
            {lanzamientos.length === 0 ? "Creá tu primer lanzamiento" : "Nuevo lanzamiento"}
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            Juntá los productos y sus fotos, y publicalos todos juntos.
          </p>
          <input
            autoFocus
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="Nombre del lanzamiento"
            className="mt-3 w-full rounded-lg border border-neutral-200 py-2 px-3 text-sm text-brand-black outline-none focus:border-neutral-400"
          />
          {/* Los MISMOS grupos y los mismos íconos que el modal de "Nuevo
              lanzamiento": es la misma decisión tomada desde otra pantalla. */}
          <div className="mt-2 space-y-2">
            {canalesPorGrupo(CANALES.filter((c) => c.id !== "mercadolibre" || ml.disponible)).map((g) => (
              <div key={g.id}>
                <p className="px-1 pb-1 text-[10px] font-medium text-neutral-400">{g.titulo}</p>
                <div className="space-y-1">
                  {g.canales.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      disabled={!c.sePuedeArmar}
                      onClick={() => setCanalNuevo(c.id)}
                      className={`w-full flex items-center gap-2.5 rounded-lg py-2 px-3 text-left transition-colors ${
                        !c.sePuedeArmar
                          ? "opacity-45 cursor-not-allowed"
                          : canalNuevo === c.id
                            ? "bg-neutral-100"
                            : "hover:bg-neutral-50"
                      }`}
                    >
                      <IconoDeCanal canal={c.id} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-brand-black">{c.nombre}</span>
                        <span className="block text-[11px] text-neutral-500">{c.nota}</span>
                      </span>
                      {canalNuevo === c.id && c.sePuedeArmar && (
                        <Check className="w-4 h-4 shrink-0 text-brand-black" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {errorLocal && <p className="mt-2 text-xs text-red-600">{errorLocal}</p>}
          <button
            type="button"
            onClick={() => void crear()}
            disabled={!nombreNuevo.trim()}
            className="mt-3 w-full rounded-lg bg-brand-black py-2.5 px-5 text-sm font-medium text-white disabled:opacity-40"
          >
            Crear y enviar
          </button>
        </div>
      ) : paso.tipo === "elegir-producto" ? (
        <div className="py-2">
          <p className="py-1 px-4 text-xs font-medium text-neutral-500">¿A qué producto va?</p>
          {paso.opciones.map((p) => (
            <button
              key={p.productoId}
              type="button"
              onClick={() => void enviar(paso.lanzamientoId, p.productoId)}
              className="w-full flex items-center gap-3 py-2 px-4 text-left hover:bg-neutral-50"
            >
              <span className="w-9 h-9 shrink-0 rounded-lg bg-neutral-100 overflow-hidden">
                {p.imagenUrl && <CachedImg src={p.imagenUrl} alt="" className="w-full h-full object-cover" />}
              </span>
              <span className="flex-1 truncate text-sm text-brand-black">{p.nombre}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="py-2">
          <p className="py-1 px-4 text-xs font-medium text-neutral-500">Enviar a</p>
          {lanzamientos.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => void seguirConProducto(l.id)}
              className="w-full flex items-center gap-3 py-2 px-4 text-left hover:bg-neutral-50"
            >
              <Rocket className="w-4 h-4 shrink-0 text-neutral-400" />
              <span className="flex-1 min-w-0">
                <span className="block truncate text-sm text-brand-black">{l.nombre}</span>
                <span className="block text-[11px] text-neutral-500">
                  {nombresDeDestinos(l.destinos)} · {l.productos} producto{l.productos === 1 ? "" : "s"}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 shrink-0 text-neutral-300" />
            </button>
          ))}
          {errorLocal && <p className="py-1 px-4 text-xs text-red-600">{errorLocal}</p>}
          <button
            type="button"
            onClick={() => {
              setNombreNuevo(sugerirNombre());
              setPaso({ tipo: "crear" });
            }}
            className="mt-1 w-full flex items-center gap-3 border-t border-neutral-100 py-2.5 px-4 text-left hover:bg-neutral-50"
          >
            <Plus className="w-4 h-4 text-neutral-400" />
            <span className="text-sm text-brand-black">Nuevo lanzamiento</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative ${contenedorClassName}`} ref={contenedorRef}>
      <button
        ref={botonRef}
        type="button"
        // Sin fotos también va apagado, aunque el host no lo haya pedido: el panel
        // no tendría qué mandar y abrirlo sería ofrecer algo que no funciona.
        disabled={disabled || fotos.length === 0}
        onClick={() => (abierto ? cerrar() : void abrir())}
        /* `whitespace-nowrap` siempre: la etiqueta de un botón no se parte en dos. */
        className={`whitespace-nowrap ${className}`}
        title={label}
      >
        <Rocket className={`${tamanoIcono} shrink-0`} />
        <span className={labelClassName}>{label}</span>
      </button>
      {/* Recién cuando la posición está medida: sin eso el primer frame lo dibuja en el
          0,0 de la pantalla y se ve saltar. */}
      {abierto && posicion && createPortal(panel, document.body)}
      {avisoPropio &&
        createPortal(
          <div className="fixed bottom-6 left-1/2 z-[200] -translate-x-1/2 px-4">
            <div className="flex items-center gap-3 rounded-2xl bg-brand-black py-3 px-5 text-sm text-white shadow-xl">
              <Check className="w-4 h-4 shrink-0 text-green-400" />
              <span>{avisoPropio.texto}</span>
              <a
                href={`/lanzamientos/${avisoPropio.id}`}
                className="shrink-0 font-medium underline underline-offset-2"
              >
                Ver
              </a>
            </div>
          </div>,
          document.body,
        )}
      <SelectorDeProducto
        abierta={sheetAbierta}
        brandId={marcaResuelta}
        onCerrar={() => setSheetAbierta(false)}
        onElegir={(productoId) => {
          if (lanzamientoPendiente) void enviar(lanzamientoPendiente, productoId);
        }}
      />
    </div>
  );
}

/** La marca activa del usuario, para cuando el host no la tiene a mano. */
async function resolverMarcaActiva(): Promise<string | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data } = await supabase
      .from("profiles")
      .select("current_brand_id")
      .eq("id", user.id)
      .single();
    return (data?.current_brand_id as string | null) ?? null;
  } catch {
    return null;
  }
}

function sugerirNombre(): string {
  const ahora = new Date();
  const mes = ahora.toLocaleDateString("es-AR", { month: "long" });
  return `Lanzamiento de ${mes.charAt(0).toUpperCase()}${mes.slice(1)}`;
}

/** Miniatura de cada producto para que elegir sea mirar, no leer. */
async function conMiniaturas(productos: ProductoResuelto[]): Promise<ProductoResuelto[]> {
  if (productos.length === 0) return productos;
  const { data } = await supabase
    .from("productos_imagenes")
    .select("variante_id, url, id_company, principal, orden")
    .in(
      "variante_id",
      productos.map((p) => p.varianteId),
    )
    .order("principal", { ascending: false })
    .order("orden", { ascending: true });
  const porVariante = new Map<string, { url: string; company: string }>();
  for (const r of ((data || []) as any[])) {
    if (porVariante.has(r.variante_id)) continue;
    porVariante.set(r.variante_id, { url: r.url, company: r.id_company });
  }
  return Promise.all(
    productos.map(async (p) => {
      const img = porVariante.get(p.varianteId);
      if (!img?.url) return p;
      // ADR-2026-08-24-tn-import-lazy: `url` puede ser el CDN de Tiendanube.
      if (/^https?:\/\//i.test(img.url)) return { ...p, imagenUrl: img.url };
      const { data: s } = await supabase.storage
        .from("productos")
        .createSignedUrl(`${img.company}/${p.varianteId}/opt/${img.url}`, 3600);
      return { ...p, imagenUrl: s?.signedUrl ?? null };
    }),
  );
}

// ── Selector de cualquier producto de la marca ─────────────────────────────

/**
 * Para las imágenes que no salieron de ningún producto (asistente libre, creativo
 * plus). Panel lateral en desktop y hoja inferior en mobile: los dos los da
 * `SheetLateral`.
 */
export function SelectorDeProducto({
  abierta,
  brandId,
  onCerrar,
  onElegir,
}: {
  abierta: boolean;
  brandId: string | null;
  onCerrar: () => void;
  onElegir: (productoId: string) => void;
}) {
  const [productos, setProductos] = useState<ProductoResuelto[]>([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState("");

  useEffect(() => {
    if (!abierta) return;
    let vivo = true;
    void (async () => {
      setCargando(true);
      // ADR-018: los productos sin marca (brand_id null) son la mitad del catálogo
      // y tienen que aparecer en TODAS las marcas.
      let q = supabase
        .from("productos")
        .select("id, nombre, brand_id, activo, variantes(id)")
        .order("updated_at", { ascending: false })
        .limit(300);
      if (brandId) q = q.or(`brand_id.eq.${brandId},brand_id.is.null`);
      const { data } = await q;
      if (!vivo) return;
      const filas = ((data || []) as any[])
        .filter((p) => p.activo !== false)
        .map((p) => {
          const v = Array.isArray(p.variantes) ? p.variantes[0] : p.variantes;
          return {
            productoId: p.id as string,
            varianteId: (v?.id as string) ?? "",
            nombre: (p.nombre as string) || "Producto",
            imagenUrl: null,
          };
        })
        .filter((p) => p.varianteId);
      setProductos(await conMiniaturas(filas));
      setCargando(false);
    })();
    return () => {
      vivo = false;
    };
  }, [abierta, brandId]);

  const visibles = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return t ? productos.filter((p) => p.nombre.toLowerCase().includes(t)) : productos;
  }, [productos, busqueda]);

  return (
    <SheetLateral open={abierta} onClose={onCerrar} titulo="¿A qué producto va?">
      <div className="px-4 pb-6">
        <p className="text-sm text-neutral-500">
          Esta imagen no se generó a partir de un producto. Elegí a cuál sumarla.
        </p>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar producto"
            className="w-full rounded-xl border border-neutral-200 py-2.5 pl-9 pr-4 text-sm text-brand-black outline-none focus:border-neutral-400"
          />
        </div>
        {cargando ? (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando productos…
          </div>
        ) : visibles.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-500">
            {busqueda ? "Ningún producto coincide" : "Todavía no tenés productos"}
          </p>
        ) : (
          <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
            {visibles.map((p) => (
              <button
                key={p.productoId}
                type="button"
                onClick={() => onElegir(p.productoId)}
                className="group text-left"
              >
                <span className="block aspect-square w-full overflow-hidden rounded-xl bg-neutral-100 group-hover:ring-2 group-hover:ring-neutral-300">
                  {p.imagenUrl && (
                    <CachedImg src={p.imagenUrl} alt="" className="w-full h-full object-cover" />
                  )}
                </span>
                <span className="mt-1 block truncate text-xs text-brand-black">{p.nombre}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </SheetLateral>
  );
}
